import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ActivityTable = ({ activities }) => {
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const activityTypeOptions = [
    { value: 'all', label: 'Alle Activiteiten' },
    { value: 'user', label: 'Gebruiker Activiteit' },
    { value: 'job', label: 'Opdracht Activiteit' },
    { value: 'financial', label: 'Financiële Activiteit' },
    { value: 'system', label: 'Systeem Activiteit' }
  ];

  const getActivityIcon = (type) => {
    const iconMap = {
      user: 'User',
      job: 'Briefcase',
      financial: 'DollarSign',
      system: 'Settings'
    };
    return iconMap?.[type] || 'Activity';
  };

  const getActivityColor = (type) => {
    const colorMap = {
      user: 'text-primary',
      job: 'text-accent',
      financial: 'text-success',
      system: 'text-muted-foreground'
    };
    return colorMap?.[type] || 'text-foreground';
  };

  const filteredActivities = filterType === 'all' 
    ? activities 
    : activities?.filter(activity => activity?.type === filterType);

  const totalPages = Math.ceil(filteredActivities?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedActivities = filteredActivities?.slice(startIndex, startIndex + itemsPerPage);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground mb-1">
              Recente Activiteiten
            </h3>
            <p className="caption text-muted-foreground">
              Platform activiteiten en gebeurtenissen
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select
              options={activityTypeOptions}
              value={filterType}
              onChange={setFilterType}
              className="w-full md:w-48"
            />
            <Button variant="outline" iconName="Download" size="sm">
              Exporteren
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 md:px-6 py-3 text-left caption font-medium text-muted-foreground">
                Type
              </th>
              <th className="px-4 md:px-6 py-3 text-left caption font-medium text-muted-foreground">
                Beschrijving
              </th>
              <th className="px-4 md:px-6 py-3 text-left caption font-medium text-muted-foreground hidden lg:table-cell">
                Gebruiker
              </th>
              <th className="px-4 md:px-6 py-3 text-left caption font-medium text-muted-foreground">
                Tijdstip
              </th>
              <th className="px-4 md:px-6 py-3 text-left caption font-medium text-muted-foreground">
                Acties
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedActivities?.map((activity) => (
              <tr key={activity?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 md:px-6 py-4">
                  <div className={`flex items-center gap-2 ${getActivityColor(activity?.type)}`}>
                    <Icon name={getActivityIcon(activity?.type)} size={18} />
                    <span className="caption font-medium hidden sm:inline">
                      {activity?.typeLabel}
                    </span>
                  </div>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <p className="text-sm text-foreground line-clamp-2">
                    {activity?.description}
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4 hidden lg:table-cell">
                  <p className="text-sm text-muted-foreground">
                    {activity?.user}
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <p className="caption text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(activity?.timestamp)}
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4">
                  <Button variant="ghost" size="sm" iconName="Eye">
                    Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="px-4 md:px-6 py-4 border-t border-border flex items-center justify-between">
          <p className="caption text-muted-foreground">
            Pagina {currentPage} van {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              Vorige
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              iconPosition="right"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Volgende
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTable;